/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage, LocalizedStandardDate } from 'intl/LocalizedComponents';
import classnames from 'classnames';
import _find from 'lodash/find';
import PropTypes from 'prop-types';
// Core components
import TableRow from 'components/core/table/TableRow';
import TableCell from 'components/core/table/TableCell';
import Tooltip from 'components/core/Tooltip';
// Models
import mInstallationLog from 'models/installationLog';
// Custom
import { listRowSpecsShape, installationLogDataShape, scenarioDataShape } from 'constants/PropTypesShapes';
import { InfoIconButton } from 'components/buttons/IconButtons';
import AppIcon from 'components/icons/AppIcon';
import TruncatedTooltipText from 'components/misc/TruncatedTooltipText';
import Link from 'components/core/Link';
import { customersShowPath } from 'installations/navigation';
import { ReactComponent as EyeSlashed } from 'assets/icons/eye-slashed.svg';
import WarningIcon from 'components/misc/WarningIcon';

const LogSource = ({ installationLog, countryCode }) => {
  const sourceIcon = mInstallationLog.sourceIcon(installationLog);
  const sourceName = mInstallationLog.sourceName(installationLog, countryCode);

  const rawLicensePlate = installationLog.read_desc;
  const interpretedLicensePlate = installationLog.desc;
  const licensesPlatesNotMatching = rawLicensePlate && interpretedLicensePlate && typeof rawLicensePlate === 'string' && typeof interpretedLicensePlate === 'string' && interpretedLicensePlate !== rawLicensePlate;

  const WarningLicencePlatesNotMaching = (
    <div>
      <LocalizedMessage id="installationLog.info.platesNotMaching" />
      <div>
        <LocalizedMessage id="installationLog.interpretedLicensePlate" />
        {' '}
        :
        {' '}
        <b>{interpretedLicensePlate}</b>
      </div>
      <div>
        <LocalizedMessage id="installationLog.readLicensePlate" />
        {' '}
        :
        {' '}
        <b>{rawLicensePlate}</b>
      </div>
    </div>
  );

  return (
    <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
      {sourceIcon && <AppIcon primary id={sourceIcon} />}
      {licensesPlatesNotMatching && (
      <Tooltip content={WarningLicencePlatesNotMaching}>
        <WarningIcon />
      </Tooltip>
      )}
      <TruncatedTooltipText>
        <LocalizedMessage id={`installationLog.type.desc.${installationLog.type}`} values={{ sourceName }} />
      </TruncatedTooltipText>
    </div>
  );
};

LogSource.propTypes = {
  installationLog: installationLogDataShape.isRequired,
  countryCode: PropTypes.string.isRequired,
};

const LogCustomer = ({ installationLog, installationId }) => {
  const customer = mInstallationLog.customer(installationLog);

  return installationLog.customer ? (
    <TruncatedTooltipText>
      <Link path={customersShowPath(installationId, installationLog.customer.id)}>{customer}</Link>
    </TruncatedTooltipText>
  ) : null;
};

LogCustomer.propTypes = {
  installationLog: installationLogDataShape.isRequired,
  installationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const LogResultScenario = ({ result, scenario, scenarioLog }) => {
  // If scenario has been deleted, get scenario name
  const scenarioName = scenario ? scenario.name : scenarioLog.scenario_name;

  return (
    <React.Fragment>
      <Tooltip content={<LocalizedMessage id={`scenarioLog.status.${scenarioLog.status}`} />}>
        <AppIcon className="mr-1" color={result.color} id={result.icon} />
      </Tooltip>
      <TruncatedTooltipText>{scenarioName}</TruncatedTooltipText>
    </React.Fragment>
  );
};

LogResultScenario.propTypes = {
  result: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
  }).isRequired,
  scenario: scenarioDataShape,
  scenarioLog: PropTypes.shape({
    scenario_name: PropTypes.string,
    status: PropTypes.string.isRequired,
  }).isRequired,
};

LogResultScenario.defaultProps = {
  scenario: null,
};

const LogResult = ({ installationLog, scenario }) => {
  const logResult = mInstallationLog.result(installationLog);
  const triggerType = mInstallationLog.scenarioTriggerType(installationLog);
  if (!logResult) return null;

  /* Trigger type */
  /* ============ */
  const TriggerType = triggerType && triggerType.value !== 'na' && (
  <Tooltip content={<LocalizedMessage id={triggerType.labelId} />}>
    <AppIcon defaultColor id={triggerType.icon} className="ml-2" />
  </Tooltip>
  );

  /* With scenario */
  /* ============= */
  const Scenario = (
    <LogResultScenario
      result={logResult}
      scenario={scenario}
      scenarioLog={installationLog.scenario_log}
    />
  );

  /* Without scenario */
  /* ================ */
  const Generic = (
    <React.Fragment>
      <AppIcon className="mr-1" color={logResult.color} id={logResult.icon} />
      <TruncatedTooltipText>
        <LocalizedMessage id={`installationLog.value.${installationLog.value}`} />
      </TruncatedTooltipText>
    </React.Fragment>
  );

  return (
    <div className="d-flex align-items-center">
      {logResult.scenario ? Scenario : Generic}
      {TriggerType}
    </div>
  );
};

LogResult.propTypes = {
  installationLog: installationLogDataShape.isRequired,
  scenario: scenarioDataShape,
};

LogResult.defaultProps = {
  scenario: null,
};

const LogRow = React.forwardRef(({
  installationLog, scenarios, countryCode, rowSpecs,
  onDisplayLogInfo, installationId,
}, ref) => {
  // Get scenario concerned by the installationLog, if any
  const scenarioLog = installationLog.scenario_log;
  const scenario = scenarioLog && scenarioLog.scenario_id && _find(scenarios, ['id', scenarioLog.scenario_id]);

  /* Flag ignored logs */
  /* ================= */
  const isIgnored = mInstallationLog.isIgnored(installationLog);
  const IgnoreIcon = isIgnored
    ? (
      <Tooltip content={<LocalizedMessage id="tooltip.ignoredLog" />}>
        <EyeSlashed fill="#808080" width="23" height="23" />
      </Tooltip>
    )
    : null;

  // Handlers
  const handleDisplayLogInfo = () => {
    onDisplayLogInfo(installationLog);
  };

  return (
    <TableRow
      ref={ref}
      element={installationLog}
      rowSpecs={rowSpecs}
      actions={[
        IgnoreIcon,
        <InfoIconButton key="bInfo" onClick={handleDisplayLogInfo} tooltipId="tooltip.logDetails" />,
      ]}
    >
      <TableCell className={classnames({ 'table-first-details': rowSpecs.withDetails })}>
        <TruncatedTooltipText>
          <LocalizedStandardDate value={installationLog.date} />
        </TruncatedTooltipText>
      </TableCell>
      <TableCell className="hidden-sm-down" noPadding>
        <LogSource installationLog={installationLog} countryCode={countryCode} />
      </TableCell>
      <TableCell className="hidden-sm-down">
        <LogCustomer installationLog={installationLog} installationId={installationId} />
      </TableCell>
      <TableCell noPadding>
        <LogResult installationLog={installationLog} scenario={scenario} />
      </TableCell>
    </TableRow>
  );
});

LogRow.displayName = 'LogRow';

LogRow.propTypes = {
  installationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  installationLog: installationLogDataShape.isRequired,
  scenarios: PropTypes.arrayOf(scenarioDataShape),
  countryCode: PropTypes.string.isRequired,
  rowSpecs: listRowSpecsShape.isRequired,
  onDisplayLogInfo: PropTypes.func.isRequired,
};

LogRow.defaultProps = {
  scenarios: null,
};

const mapStateToProps = (state) => ({
  scenarios: state.installationScenario.list.elements,
  countryCode: state.intl.countryCode,
});

module.exports = compose(
  connect(
    mapStateToProps,
    null,
  ),
)(LogRow);



// WEBPACK FOOTER //
// ./routes/app/routes/installations/assets/logs/LogRow.js